/**
 *
 * @module paymentMethodResponse
 *
 */

import Check from 'check-types';
import BillingAddress from './billingAddress';
import PaymentCardType from './paymentCardType';
import PaymentMethodType from './paymentMethodType';

import InvalidArgumentException from './../exception/invalidArgumentException';

const {
    nonEmptyObject,
    nonEmptyString,
    number,
    instanceStrict,
    boolean
} = Check.assert;

/**
 *
 * @class PaymentMethodResponse
 *
 */
export default class PaymentMethodResponse {

    /**
     *
     * @param {Object} options
     * @param {String} [options.alternateName=undefined]
     * @param {BillingAddress} options.billingAddress
     * @param {String} [options.brand=undefined]
     * @param {String} [options.cardType=undefined]
     * @param {String} [options.country=undefined]
     * @param {Number} [options.expiryMonth=undefined]
     * @param {Number} [options.expiryYear=undefined]
     * @param {String} [options.firstSix=undefined]
     * @param {Boolean} [options.isCorporate=false]
     * @param {Boolean} [options.isDefault=false]
     * @param {String} [options.issuer=undefined]
     * @param {String} [options.lastFour=undefined]
     * @param {String} [options.ownerFullName=undefined]
     * @param {String} options.paymentMethodId
     * @param {String} options.walletId
     * @param {String} options.type
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options = {}) {

        const errorMessage = {
            ctor: 'PaymentMethodResponse(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get alternateName() {
                return `${this.ctor} alternateName should be a non-empty String`;
            },
            get billingAddress() {
                return `${this.ctor} billingAddress should be an instance of SDK.Services.Commerce.BillingAddress`;
            },
            get brand() {
                return `${this.ctor} brand should be a non-empty String`;
            },
            get cardType() {
                return `${this.ctor} cardType should be a non-empty String`;
            },
            get country() {
                return `${this.ctor} country should be a non-empty String`;
            },
            get expiryMonth() {
                return `${this.ctor} expiryMonth should be a Number`;
            },
            get expiryYear() {
                return `${this.ctor} expiryYear should be a Number`;
            },
            get firstSix() {
                return `${this.ctor} firstSix should be a non-empty String`;
            },
            get isCorporate() {
                return `${this.ctor} isCorporate should be a Boolean`;
            },
            get isDefault() {
                return `${this.ctor} isDefault should be a Boolean`;
            },
            get issuer() {
                return `${this.ctor} issuer should be a non-empty String`;
            },
            get lastFour() {
                return `${this.ctor} lastFour should be a non-empty String`;
            },
            get ownerFullName() {
                return `${this.ctor} ownerFullName should be a non-empty String`;
            },
            get paymentMethodId() {
                return `${this.ctor} paymentMethodId should be a non-empty String`;
            },
            get walletId() {
                return `${this.ctor} walletId should be a non-empty String`;
            },
            get type() {
                return `${this.ctor} type should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { alternateName, billingAddress, brand, cardType, country, expiryMonth } = options;
        const { expiryYear, firstSix, isCorporate = false, isDefault = false, issuer, lastFour } = options;
        const { ownerFullName, paymentMethodId, walletId, type = PaymentMethodType.PaymentCard } = options;

        if (Check.assigned(alternateName)) {
            nonEmptyString(alternateName, errorMessage.alternateName, InvalidArgumentException);
        }

        instanceStrict(billingAddress, BillingAddress, errorMessage.billingAddress, InvalidArgumentException);

        if (Check.assigned(brand)) {
            nonEmptyString(brand, errorMessage.brand, InvalidArgumentException);
        }

        if (Check.assigned(cardType)) {
            nonEmptyString(cardType, errorMessage.cardType, InvalidArgumentException);
        }

        if (Check.assigned(country)) {
            nonEmptyString(country, errorMessage.country, InvalidArgumentException);
        }

        if (Check.assigned(expiryMonth)) {
            number(expiryMonth, errorMessage.expiryMonth, InvalidArgumentException);
        }

        if (Check.assigned(expiryYear)) {
            number(expiryYear, errorMessage.expiryYear, InvalidArgumentException);
        }

        if (Check.assigned(firstSix)) {
            nonEmptyString(firstSix, errorMessage.firstSix, InvalidArgumentException);
        }

        boolean(isCorporate, errorMessage.isCorporate, InvalidArgumentException);
        boolean(isDefault, errorMessage.isDefault, InvalidArgumentException);

        if (Check.assigned(issuer)) {
            nonEmptyString(issuer, errorMessage.issuer, InvalidArgumentException);
        }

        if (Check.assigned(lastFour)) {
            nonEmptyString(lastFour, errorMessage.lastFour, InvalidArgumentException);
        }

        if (Check.assigned(ownerFullName)) {
            nonEmptyString(ownerFullName, errorMessage.ownerFullName, InvalidArgumentException);
        }

        nonEmptyString(paymentMethodId, errorMessage.paymentMethodId, InvalidArgumentException);
        nonEmptyString(walletId, errorMessage.walletId, InvalidArgumentException);
        nonEmptyString(type, errorMessage.type, InvalidArgumentException);

        /**
         *
         * @access public
         * @type {String|undefined}
         *
         */
        this.alternateName = alternateName;

        /**
         *
         * @access public
         * @type {BillingAddress}
         *
         */
        this.billingAddress = billingAddress;

        /**
         *
         * @access public
         * @type {String|undefined}
         *
         */
        this.brand = brand;

        /**
         *
         * @access public
         * @type {SDK.Services.Commerce.PaymentCardType|undefined}
         *
         */
        this.cardType = PaymentCardType[cardType];

        /**
         *
         * @access public
         * @type {String|undefined}
         *
         */
        this.country = country;

        /**
         *
         * @access public
         * @type {Number|undefined}
         *
         */
        this.expiryMonth = expiryMonth;

        /**
         *
         * @access public
         * @type {Number|undefined}
         *
         */
        this.expiryYear = expiryYear;

        /**
         *
         * @access public
         * @type {String|undefined}
         *
         */
        this.firstSix = firstSix;

        /**
         *
         * @access public
         * @type {Boolean}
         *
         */
        this.isCorporate = isCorporate;

        /**
         *
         * @access public
         * @type {Boolean}
         *
         */
        this.isDefault = isDefault;

        /**
         *
         * @access public
         * @type {String|undefined}
         *
         */
        this.issuer = issuer;

        /**
         *
         * @access public
         * @type {String|undefined}
         *
         */
        this.lastFour = lastFour;

        /**
         *
         * @access public
         * @type {String|undefined}
         *
         */
        this.ownerFullName = ownerFullName;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.paymentMethodId = paymentMethodId;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.walletId = walletId;

        /**
         *
         * @access public
         * @type {String}
         * @desc The type of payment method. "PaymentCard" for a Credit Card, "PayPalExpress" for PayPal,
         * "IDealPayment" for IDeal.
         * @note Only 'PaymentCard' is supported at this time.
         *
         */
        this.type = type;

    }

}



// WEBPACK FOOTER //
// ./src/services/commerce/paymentMethodResponse.js